import { Paper, Popper, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { TextBox } from '@superdispatch/ui-lab';
import { useField, useFormikContext } from 'formik';
import { ChangeEvent, FocusEvent, KeyboardEvent } from 'react';
import styled from 'styled-components';
import { VehicleMakeDTO } from '../../../data/VehicleDTO';
import {
  useVehicleMakes,
  useVehiclePopularMakes,
} from '../../../data/VehiclesAPI';
import useDebounce from '../../../hooks/useDebounce';

const StyledPaper = styled(Paper)`
  & .MuiAutocomplete-groupLabel {
    color: #5b6371;
    font-weight: 400;
    line-height: 20px;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  & .MuiListSubheader-sticky {
    position: static;
  }
`;

const StyledPopper = styled(Popper)`
  min-width: 160px;
`;

export function focusNextInput(currentInputName: string) {
  const currentInput = document.querySelector(
    `input[name="${currentInputName}"]`,
  ) as HTMLInputElement;

  const focusableElements = document.querySelectorAll(
    'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([aria-label=“Clear”]), div[role=“button”]:not([disabled])',
  );

  const currentIndex = Array.from(focusableElements).indexOf(currentInput);
  if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
    (focusableElements[currentIndex + 1] as HTMLElement).focus();
  }
}

interface VehicleMakeAutocompleteFieldProps {
  name: string;
  disabled?: boolean;
  onChange: (value: VehicleMakeDTO) => void;
}

export function VehicleMakeAutocompleteField({
  name,
  onChange,
  disabled,
}: VehicleMakeAutocompleteFieldProps) {
  const { isSubmitting } = useFormikContext();
  const [{ value, onBlur }, { error, touched }, { setValue }] =
    useField<string>({
      name,
    });

  const debouncedQuery = useDebounce(value?.trim(), 300);
  const { data } = useVehicleMakes(debouncedQuery);
  const { data: popularMakes } = useVehiclePopularMakes();
  const errorText = touched && error;

  const getOptionLabel = (option: string | VehicleMakeDTO) => {
    if (typeof option === 'string') {
      return option;
    }
    return option.name;
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    onBlur(event.target.value);
  };

  const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  const handleChange = (nextValue: string | VehicleMakeDTO) => {
    const make =
      typeof nextValue !== 'string'
        ? nextValue
        : data?.find((x) => x.name.toLowerCase() === nextValue.toLowerCase()) ||
          popularMakes?.find(
            (x) => x.name.toLowerCase() === nextValue.toLowerCase(),
          );

    if (!make) return;

    onChange(make);
    setValue(make.name);
    focusNextInput(name);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      focusNextInput(name);
    }
  };

  const groupOptions = (option: string | VehicleMakeDTO) => {
    if (typeof option === 'string') return '';

    return option.is_popular ? 'Popular Makes' : 'Other Makes';
  };

  return (
    <Autocomplete
      loading={!data}
      options={data || popularMakes || []}
      openOnFocus={true}
      groupBy={groupOptions}
      freeSolo={true}
      forcePopupIcon={false}
      multiple={false}
      selectOnFocus={true}
      disabled={isSubmitting || disabled}
      disableClearable={true}
      value={value || ''}
      getOptionLabel={getOptionLabel}
      onBlur={handleBlur}
      onInput={handleInput}
      onChange={(_, value) => {
        handleChange(value);
      }}
      onKeyDown={handleKeyDown}
      PaperComponent={StyledPaper}
      PopperComponent={StyledPopper}
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          error={!!errorText}
          helperText={errorText}
          label={
            <>
              Make <TextBox color="secondary">(Required)</TextBox>
            </>
          }
        />
      )}
    />
  );
}
